import { useEffect } from 'react'
import { Alert, Col, DatePicker, Form, Input, Modal, Row, Select, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import type { OrgNode, OrgNodeType } from '@/features/org/orgApi'
import { OrgNodeTreeSelect } from './OrgNodeTreeSelect'
import { withCurrentOption } from '@/shared/ui/currentOption'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  EMPLOYMENT_TYPES,
  REASON_MAX,
  assignEmployment,
  type EmploymentType,
  type JobTitle,
  type Person,
} from './hrApi'

const DATE_FORMAT = 'YYYY-MM-DD'

interface EmploymentAssignModalProps {
  open: boolean
  person: Person
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  jobTitles: readonly JobTitle[]
  nodesReliable: boolean
  onClose: () => void
}

interface FormValues {
  orgNodeId: string
  jobTitleId: string
  employmentType: EmploymentType
  validFrom: Dayjs
  reason: string
}

/**
 * Yangi tayinlash — lavozim, bo'lim yoki ish turini o'zgartirish.
 *
 * <h2>⚠️ BU "TAHRIRLASH" EMAS, YANGI DAVR</h2>
 * Server ochiq davrni YOPADI va yangisini OCHADI
 * (`EmploymentController#assign`). Eski qator o'zgarmaydi va
 * o'chirilmaydi — bazada uni `hr.employment_immutable()` triggeri
 * qo'riqlaydi.
 *
 * Shuning uchun oynaning sarlavhasi ham, tugmasi ham "saqlash" emas,
 * "tayinlash". Bu farq matn o'yini emas: operator "tahrirlayapman" deb
 * o'ylasa, sabab maydonini formal to'ldirib qo'yadi — holbuki aynan
 * o'sha matn keyin "nega bu odam mart oyida Kabulga o'tgan?" degan
 * savolga yagona javob bo'ladi.
 *
 * <h2>⚠️ ALOHIDA HUQUQ — `hr.employment.write`</h2>
 * Kadr yozuvini tahrirlashdan (`hr.person.write`) ajratilgan, chunki
 * xodimni boshqa filialga ko'chirish UNI KIM KO'RISHINI o'zgartiradi
 * (`SUBTREE` doirasi).
 *
 * <h2>⚠️ SANA — DAVR BOSHLANADIGAN KUN</h2>
 * Server oraliqlar kesishmasligini bazada tekshiradi
 * (`hr.employment_guard`): oldingi davrning boshlanishidan oldingi sana
 * rad etiladi. Shuning uchun bu yerda o'tmish TAQIQLANMAYDI (orqaga
 * sana bilan tayinlash haqiqiy hol — buyruq kechikib yetib keladi),
 * lekin server xatosi tushunarli matn bilan ko'rsatiladi.
 */
export function EmploymentAssignModal({
  open,
  person,
  nodes,
  types,
  jobTitles,
  nodesReliable,
  onClose,
}: EmploymentAssignModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      // ⚠️ JORIY qiymatlar boshlang'ich sifatida qo'yiladi: odatda
      // faqat BITTASI o'zgaradi (lavozim ko'tarildi, bo'lim o'zgardi).
      // Bo'sh formadan boshlash operatorni o'zgarmagan maydonlarni
      // qayta tanlashga majburlardi va xato ehtimolini oshirardi.
      // ⚠️ `null` — tayinlashsiz xodim (`Person.orgNodeId` izohi). U holda
      // maydon BO'SH ochiladi: operator tugunni o'zi tanlaydi.
      ...(person.orgNodeId ? { orgNodeId: person.orgNodeId } : {}),
      ...(person.jobTitleId ? { jobTitleId: person.jobTitleId } : {}),
      // ⚠️ `null` — faqat tayinlashsiz xodimda (`LEFT JOIN LATERAL`),
      // ya'ni amalda uchramaydi: tayinlash yaratishda majburiy.
      // "Noma'lum qiymat" holati esa endi UMUMAN yo'q — backend
      // `EmploymentType` enum, baza `employment_type_valid` CHECK.
      employmentType: person.employmentType ?? 'FULL_TIME',
      // ⚠️ TENANT kalendaridagi bugun (`dayRange.ts`)
      validFrom: tenantToday(),
      reason: '',
    })
  }, [open, person, form])

  const assign = useMutation({
    mutationFn: (values: FormValues) =>
      assignEmployment(person.id, {
        orgNodeId: values.orgNodeId,
        jobTitleId: values.jobTitleId,
        employmentType: values.employmentType,
        validFrom: values.validFrom.format(DATE_FORMAT),
        reason: values.reason.trim(),
      }),
    onSuccess: () => {
      // ⚠️ `hr.all` PREFIKS: tayinlash tarixni ham, kartochkani ham,
      // ro'yxatni ham eskirtiradi (joriy tugun va lavozim `PersonView`
      // ichida keladi). Faqat `history` ni bekor qilish kartochkada
      // eski bo'limni qoldirardi — jimgina.
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const activeJobTitles = jobTitles.filter((title) => title.active)

  /**
   * ⚠️ FILTR YOLG'IZ YETMAYDI (N8, 2026-08-15).
   *
   * Qiymat bu yerga TASHQARIDAN keladi — yuqoridagi `setFieldsValue`
   * xodimning JORIY lavozimini qo'yadi. Lavozim keyin nofaol qilingan
   * bo'lsa (`hr.job_title.active = false`; o'chirish YO'Q —
   * `employment_job_title_fk` `RESTRICT`), u ro'yxatda umuman
   * bo'lmaydi: sahifalar `fetchJobTitles(true)` bilan FAQAT faollarini
   * so'raydi. Mos `option` topilmaganda antd `Select` XOM `value` ni
   * chizadi va ekranga UUID chiqadi.
   *
   * Bu ayni nuqson `StockMovementModal` da Staging'da KO'RINGAN holda
   * o'lchandi; bu yerda hozircha LATENT (o'lchandi: nofaol lavozim
   * bor, uni tutib turgan xodim yo'q), lekin mexanizm bir xil.
   *
   * ⚠️ Yorliq KATALOGDAN emas, XODIM yozuvidan olinadi
   * (`person.jobTitleName`) — katalogda bu yozuv yo'q, ya'ni boshqa
   * manba ham yo'q.
   *
   * ⚠️ `disabled: true` — katalog qoidasi «yangi tayinlashda TANLAB
   * bo'lmaydi» (`JobTitleDraft#active`, `hr.jobTitle.activeHint`).
   * Oldindan qo'yilgan qiymat esa tanlov emas, mavjud holat: uni
   * yuborish TO'SILMAYDI (server ham qabul qiladi), aks holda
   * «faqat bo'limni o'zgartiraman» degan operator lavozimni ham
   * almashtirishga majbur bo'lardi.
   */
  const jobTitleId = Form.useWatch('jobTitleId', form)
  const jobTitleOptions = withCurrentOption(
    activeJobTitles.map((title) => ({ value: title.id, label: seededName('jobTitles', title.code, title.name, t) })),
    jobTitleId,
    (id) =>
      id === person.jobTitleId && person.jobTitleName !== null
        ? {
            value: id,
            // ⚠️ «Nofaol» BELGISI faqat katalog haqiqatan o'qilgan
            // bo'lsa qo'yiladi. Ro'yxat bo'sh bo'lishining ikkinchi
            // sababi bor — so'rov YIQILGAN, va u holda "nofaol" deb
            // yozish YOLG'ON bo'lardi (`guards.mjs` 3-bo'lim).
            label:
              activeJobTitles.length > 0
                ? `${person.jobTitleName} · ${t('hr.jobTitle.stateInactive')}`
                : person.jobTitleName,
            disabled: activeJobTitles.length > 0,
          }
        : null,
  )

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    assign.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.assignment.assignTitle', { name: person.fullName })}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => assign.mutate(values))}
      confirmLoading={assign.isPending}
      okText={t('hr.assignment.assignAction')}
      cancelText={t('common.cancel')}
      okButtonProps={{ disabled: !nodesReliable }}
      destroyOnHidden
      width={640}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {assign.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(assign.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={assign.isPending}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.assignment.newPeriodNote')}
        />

        {nodesReliable ? null : (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('hr.person.nodesUnavailable')}
          />
        )}

        {/* Joriy holat — operator nimadan nimaga o'tayotganini ko'rsin */}
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {t('hr.assignment.currentNote', {
            node: person.orgNodeName ?? person.orgNodeCode ?? '—',
            jobTitle: person.jobTitleName ?? '—',
          })}
        </Typography.Paragraph>

        <Form.Item
          name="orgNodeId"
          label={t('hr.assignment.orgNode')}
          extra={t('hr.assignment.orgNodeHint')}
          rules={[{ required: true, message: t('hr.assignment.validation.nodeRequired') }]}
        >
          <OrgNodeTreeSelect nodes={nodes} types={types} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="jobTitleId"
              label={t('hr.assignment.jobTitle')}
              rules={[{ required: true, message: t('hr.assignment.validation.jobTitleRequired') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                notFoundContent={t('hr.jobTitle.empty')}
                options={jobTitleOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="employmentType"
              label={t('hr.assignment.employmentType')}
              rules={[{ required: true, message: t('hr.assignment.validation.typeRequired') }]}
            >
              <Select
                options={EMPLOYMENT_TYPES.map((type) => ({
                  value: type,
                  label: t(`employmentType.${type}`),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="validFrom"
          label={t('hr.assignment.validFrom')}
          extra={t('hr.assignment.validFromHint')}
          rules={[{ required: true, message: t('hr.assignment.validation.fromRequired') }]}
        >
          <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="reason"
          label={t('hr.assignment.reason')}
          extra={t('hr.assignment.reasonHint')}
          rules={[
            { required: true, message: t('hr.assignment.validation.reasonRequired') },
            { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
          ]}
        >
          <Input.TextArea rows={3} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  )
}
